import React, { useCallback, useEffect, useRef, useState } from 'react'
import BroadcastPlayer from './BroadcastPlayer'
import BrandAppearanceTable from './BrandAppearanceTable'
import { Loader2, ImageOff, Tag, MapPin, Undo2, Merge, PlayCircle, Image as ImageIcon, UserPlus } from 'lucide-react'
import Drawer from '../ui/Drawer'
import { useToast } from '../../contexts/ToastContext'
import {
  BrandAppearance, ReviewBrand, RefZone, ExclusionReason
} from '../../services/broadcastAdminService'
import {
  secs, pct, conf, areaSec, BrandReviewSource, MARK_LABELS, MARK_OPTIONS, REASONS
} from './broadcastReviewConstants'

interface Props {
  /** The other brands on this broadcast, as merge targets. */
  candidates?: { brand_key: string; brand_name: string; is_sponsor?: boolean }[]
  brand: ReviewBrand | null
  customerId: string
  broadcastId: string
  readOnly?: boolean
  source: BrandReviewSource
  /** so the brand card's zone summary reflects a correction */
  onZoneChanged?: () => void
  /**
   * Offered for an UNSPONSORED brand only: the caller opens Add Sponsor
   * pre-filled from this brand. Absent, the action is not shown.
   */
  onMakeSponsor?: (brand: ReviewBrand) => void
  onClose: () => void
}

/**
 * Every appearance of one brand, biggest first.
 *
 * A brand card answers "is this real?" for the best frame. This answers the
 * harder question — "is it real EVERY time?" — which is where a resolver that
 * snapped two different boards onto one name shows itself.
 */
const BroadcastBrandDrawer: React.FC<Props> = ({
  brand, customerId, broadcastId, readOnly = false, source, candidates = [],
  onZoneChanged, onMakeSponsor, onClose
}) => {
  const { showError, showSuccess } = useToast()
  const [rows, setRows] = useState<BrandAppearance[]>([])
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState<BrandAppearance | null>(null)
  const [imgState, setImgState] = useState<'loading' | 'ok' | 'error'>('loading')
  // Watch vs look. The frame answers "is this real?"; the video answers
  // "what was happening?" — which is the question a board that only
  // appears during a replay wipe actually needs. Off by default: the
  // still is faster and is what most adjudications need.
  const [showVideo, setShowVideo] = useState(false)
  const [zones, setZones] = useState<RefZone[]>([])
  const [savingZone, setSavingZone] = useState(false)
  const [savingMark, setSavingMark] = useState(false)
  const [excludingId, setExcludingId] = useState<string | null>(null)
  // What a single-appearance exclusion is recorded as. It used to be fixed
  // at 'misread', so every excluded frame claimed a reason nobody chose.
  const [appearanceReason, setAppearanceReason] = useState<ExclusionReason>('misread')
  const [merging, setMerging] = useState(false)
  const [unmerging, setUnmerging] = useState<string | null>(null)

  /**
   * Re-selecting the row that is already showing must be a no-op. Setting
   * imgState back to 'loading' without changing src or key leaves the <img>
   * mounted and already loaded, so no onLoad ever fires and the spinner runs
   * forever.
   */
  // A SPONSOR'S DELIVERY IS NOT EDITABLE. The server refuses it too; this
  // stops the reviewer discovering that by being told no. Exclusion exists
  // for a misread, and a brand that resolves to an active partner is
  // evidence of what somebody was delivered — removing it removes the
  // evidence, and makes "delivered nothing" indistinguishable from "never
  // detected".
  const sponsored = brand?.is_sponsored === true

  const select = (row: BrandAppearance) => {
    if (row.id === active?.id) return
    setActive(row)
    setImgState('loading')
  }

  const load = useCallback(async (name: string) => {
    setLoading(true)
    try {
      const res = await source.getAppearances(customerId, broadcastId, name)
      const list = res.data || []
      setRows(list)
      setActive(list[0] ?? null)
      setImgState('loading')
    } catch (err) {
      showError('Could not load appearances', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setLoading(false)
    }
  }, [customerId, broadcastId, showError])

  useEffect(() => {
    if (brand) void load(brand.brand_name)
    else { setRows([]); setActive(null) }
  }, [brand, load])

  // The zone list is the prompt's enum, so it is fetched once per broadcast
  // rather than per brand — but it is now SPORT-SCOPED, and this drawer stays
  // mounted while the review page's picker moves between broadcasts. Caching
  // on `zones.length` alone would show a basketball game the baseball list it
  // happened to load first.
  const zonesFor = useRef<string | null>(null)
  useEffect(() => {
    if (!brand || zonesFor.current === broadcastId) return
    zonesFor.current = broadcastId
    source.listZones(broadcastId)
      .then((res) => setZones(res.data || []))
      .catch(() => { /* the picker degrades to read-only; not worth a toast */ })
  }, [brand, broadcastId])

  /**
   * mark_type is a GLOBAL fact, so this writes mark_source = 'reviewer' and
   * the automatic classifier will never overwrite it. Empty string clears the
   * override and hands the brand back to the automatic tiers.
   */
  const changeMark = async (value: string) => {
    if (!brand || !source.setMark) return
    setSavingMark(true)
    try {
      await source.setMark(customerId, broadcastId, brand.brand_name,
        value === '' ? null : (value as any))
      onZoneChanged?.()
    } catch (err) {
      showError('Could not set the mark type',
        err instanceof Error ? err.message : 'Please try again')
    } finally { setSavingMark(false) }
  }

  /**
   * Exclude or restore ONE occurrence. The brand keeps its other appearances
   * and its place in the list; only this row stops contributing to any total.
   */
  const toggleAppearance = async (appearanceId: string, currentlyExcluded: boolean) => {
    if (!source.setAppearanceExclusion) return
    setExcludingId(appearanceId)
    try {
      await source.setAppearanceExclusion(
        customerId, broadcastId, appearanceId, currentlyExcluded ? null : appearanceReason
      )
      setRows((prev) => prev.map((r) => r.id === appearanceId
        ? { ...r, excluded_reason: currentlyExcluded ? null : appearanceReason } : r))
      showSuccess(
        currentlyExcluded ? 'Occurrence restored' : 'Occurrence excluded',
        currentlyExcluded
          ? 'It counts toward this brand again.'
          : 'It no longer counts toward this brand, the placement chart or the broadcast totals.'
      )
      onZoneChanged?.()
    } catch (err) {
      showError('Could not update the occurrence',
        err instanceof Error ? err.message : 'Please try again')
    } finally { setExcludingId(null) }
  }

  /**
   * Fold this brand into another. Per customer and applied at read time, so it
   * takes effect on every broadcast they have — including ones already
   * published, which is the point: one sponsor reported two ways across two
   * reports is the problem being fixed.
   */
  const merge = async (intoKey: string) => {
    if (!brand || !source.mergeBrand || !intoKey) return
    const target = candidates.find((c) => c.brand_key === intoKey)
    setMerging(true)
    try {
      await source.mergeBrand(customerId, brand.brand_key, intoKey, target?.brand_name)
      showSuccess('Brands merged',
        `“${brand.brand_name}” now reports as “${target?.brand_name ?? intoKey}” on every broadcast.`)
      onZoneChanged?.()
      onClose()
    } catch (err) {
      showError('Could not merge', err instanceof Error ? err.message : 'Please try again')
    } finally { setMerging(false) }
  }

  /** Undo one merge. Reachable only from the CANONICAL row, because the alias
   *  no longer has a row of its own — which is why it carries its aliases. */
  const unmerge = async (aliasKey: string) => {
    if (!source.unmergeBrand) return
    setUnmerging(aliasKey)
    try {
      await source.unmergeBrand(customerId, aliasKey)
      showSuccess('Un-merged', `“${aliasKey}” reports on its own again.`)
      onZoneChanged?.()
      onClose()
    } catch (err) {
      showError('Could not un-merge', err instanceof Error ? err.message : 'Please try again')
    } finally { setUnmerging(null) }
  }

  const changeZone = async (zoneCode: string) => {
    if (!active || zoneCode === active.zone_code) return
    setSavingZone(true)
    try {
      await source.setZone(customerId, broadcastId, active.id, zoneCode)
      setRows((prev) => prev.map((r) => r.id === active.id
        ? { ...r, zone_code: zoneCode, zone_code_original: r.zone_code_original ?? r.zone_code }
        : r))
      setActive((prev) => prev && {
        ...prev, zone_code: zoneCode, zone_code_original: prev.zone_code_original ?? prev.zone_code
      })
      showSuccess('Placement saved',
        `This appearance is now recorded at ${zones.find((z) => z.code === zoneCode)?.label ?? zoneCode}.`)
      onZoneChanged?.()
    } catch (err) {
      showError('Could not change placement', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setSavingZone(false)
    }
  }

  return (
    <Drawer
      isOpen={!!brand}
      onClose={onClose}
      title={brand?.display_name ?? brand?.brand_name ?? ''}
      description={brand ? `${brand.appearances} appearance${brand.appearances === 1 ? '' : 's'} · ${secs(brand.total_duration_ms)} on screen` : ''}
      icon={<Tag className="h-5 w-5" />}
      width="xl"
    >
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading appearances…
        </div>
      ) : (
        <div className="space-y-4">
          {/* The frame under examination, as large as the drawer allows —
              squinting at a thumbnail is how a misread gets published. */}
          <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
            {active && showVideo ? (
              <BroadcastPlayer
                fetchUrl={async () => (await source.getPlaybackUrl(customerId, broadcastId)).data}
                startMs={active.peak_frame_ms ?? active.start_time_ms ?? 0}
                className="w-full h-full"
              />
            ) : active ? (
              <>
                <img
                  key={active.id}
                  ref={(el) => {
                    // A frame already in the browser cache can finish before
                    // React attaches onLoad, which would strand the spinner the
                    // same way. `complete` is the only signal that survives that
                    // race.
                    if (el?.complete && el.naturalWidth > 0) setImgState('ok')
                  }}
                  src={source.getFrameUrl(customerId, broadcastId, active.id)}
                  alt={`${brand?.brand_name} at ${secs(active.peak_frame_ms ?? active.start_time_ms)}`}
                  className="w-full h-full object-contain"
                  crossOrigin="use-credentials"
                  onLoad={() => setImgState('ok')}
                  onError={() => setImgState('error')}
                />
                {imgState === 'loading' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-[11px]">Pulling the frame from the source video…</span>
                  </div>
                )}
                {imgState === 'error' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-500">
                    <ImageOff className="h-6 w-6" />
                    <span className="text-[11px]">Frame unavailable</span>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                No appearances
              </div>
            )}
          </div>

          {active && (
            <div className="flex justify-end -mt-2">
              <button
                onClick={() => setShowVideo((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              >
                {showVideo
                  ? (<><ImageIcon className="h-3 w-3" /> Show the frame</>)
                  : (<><PlayCircle className="h-3 w-3" /> Replay from {secs(active.peak_frame_ms ?? active.start_time_ms)}</>)}
              </button>
            </div>
          )}

          {active && (
            <>
              {/* The same four figures as the table row, in the same units.
                  This block showed confidence as "0.86" while the table now
                  shows "86%", and omitted area-seconds entirely — two
                  readings of one appearance that a reviewer would have had
                  to reconcile in their head. */}
              <div className="grid grid-cols-4 gap-3 text-xs">
                {[
                  ['At', secs(active.peak_frame_ms ?? active.start_time_ms)],
                  ['Area-sec', areaSec(active.area_seconds)],
                  ['Max size', pct(active.max_area_share)],
                  ['Confidence', conf(active.avg_confidence)]
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-gray-400 uppercase tracking-wider text-[10px] font-semibold">{k}</p>
                    <p className="font-semibold text-gray-900 truncate" title={v}>{v}</p>
                  </div>
                ))}
              </div>



              {/* The other way to make a brand count: it IS a sponsor nobody
                  has entered yet. Merging into an existing sponsor (below)
                  covers "it is a misspelling of one we have". */}
              {onMakeSponsor && brand && !readOnly && !sponsored && (
                <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3">
                  <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                    <UserPlus className="h-3.5 w-3.5 text-blue-500" />Is this a sponsor?
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                    Create it as a sponsor and its exposure moves to Sponsor Exposure, on
                    this and every other broadcast.
                  </p>
                  <button
                    type="button"
                    onClick={() => onMakeSponsor(brand)}
                    className="mt-2 inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
                  >
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />Add as sponsor
                  </button>
                </div>
              )}

              {/* A SPONSORED BRAND CANNOT BE MERGED AWAY, so the control is
                  absent rather than disabled. This picker makes the open
                  brand the ALIAS and the sponsor is resolved from the
                  surviving key, so merging one under another name either
                  hands its exposure to a different sponsor or detaches it
                  entirely. Folding a misread INTO a sponsor is the useful
                  direction and is done from the misread's own drawer, which
                  is unaffected. */}
              {source.mergeBrand && !readOnly && !sponsored &&
                (candidates.length > 0 || (brand?.merged_keys?.length ?? 0) > 0) && (
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label htmlFor="merge-picker"
                           className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <Merge className="h-3.5 w-3.5 text-gray-400" />Merge into another brand
                    </label>
                    {merging && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                  </div>
                  <select
                    id="merge-picker"
                    value=""
                    disabled={merging}
                    onChange={(e) => void merge(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-800 disabled:opacity-50"
                  >
                    <option value="">Not merged — reports on its own</option>
                    {/* Alphabetical. These arrived in the server's order,
                        which is by area - useful for a ranking, useless for
                        finding "Rawlings" in a list of 24. filter() returns a
                        copy, so sorting here mutates nothing the parent owns. */}
                    {candidates
                      .filter((c) => c.brand_key !== brand?.brand_key)
                      .sort((a, b) =>
                        a.brand_name.localeCompare(b.brand_name, undefined, { sensitivity: 'base' }))
                      .map((c) => (
                        <option key={c.brand_key} value={c.brand_key}>
                          {c.brand_name}{c.is_sponsor ? ' (sponsor)' : ''}
                        </option>
                      ))}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">
                    For a second spelling of the same brand. Folds this brand into the one
                    you pick, on every broadcast for this customer, and can be undone from
                    that brand. Pick a sponsor to credit this exposure to them.
                  </p>

                  {/* The aliases folded into THIS row. Undoing has to live here:
                      a merged brand has no row of its own to click. */}
                  {(brand?.merged_keys?.length ?? 0) > 0 && (
                    <div className="mt-2.5 pt-2.5 border-t border-gray-100">
                      <p className="text-[11px] font-semibold text-gray-600 mb-1.5">
                        Reported together with
                      </p>
                      <div className="space-y-1">
                        {brand!.merged_keys.map((k) => (
                          <div key={k} className="flex items-center justify-between gap-2 text-[11px]">
                            <span className="text-gray-600 font-mono truncate">{k}</span>
                            <button
                              type="button"
                              disabled={unmerging === k}
                              onClick={() => void unmerge(k)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 disabled:opacity-40 flex-shrink-0"
                            >
                              {unmerging === k
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <Undo2 className="h-3 w-3" />}
                              Separate
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Support only: mark_type says what the brand IS, globally, so a
                  customer must not be able to assert it for everyone else. The
                  customer source omits setMark and this never renders.

                  Absent for a SPONSORED brand. mark_type only decides whether
                  a NON-partner brand counts - resolving to an active partner
                  counts whatever this field says - so on a sponsored brand
                  every option here is inert. A naming-rights venue is the
                  case that proves it: classified venue, still inventory. */}
              {source.setMark && !sponsored && (
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label htmlFor="mark-picker"
                           className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <Tag className="h-3.5 w-3.5 text-gray-400" />What this brand is
                    </label>
                    {savingMark && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                  </div>
                  <select
                    id="mark-picker"
                    value={brand?.mark_type ?? ''}
                    disabled={readOnly || savingMark}
                    onChange={(e) => void changeMark(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-800 disabled:opacity-50"
                  >
                    {/* The default is already "counts as a sponsor", so this
                        is not a problem state and needs no action. Choosing
                        Sponsor explicitly is different: it PINS the brand
                        against the automatic classifier. */}
                    <option value="">Commercial brand — assumed, nobody has classified it</option>
                    {/* Sorted by the LABEL, which is what the reader scans -
                        sorting the enum values would put "sponsor" under S
                        while it reads "Commercial brand". Copied first:
                        MARK_OPTIONS is a module-level const and sort mutates
                        in place, which would reorder it for every later
                        render in the session. */}
                    {[...MARK_OPTIONS]
                      .sort((a, b) =>
                        MARK_LABELS[a].localeCompare(MARK_LABELS[b], undefined, { sensitivity: 'base' }))
                      .map((t) => (
                        <option key={t} value={t}>{MARK_LABELS[t]}</option>
                      ))}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">
                    {brand?.is_promoted
                      ? 'On this customer\u2019s partner list, so it counts toward their exposure whatever it is.'
                      : brand?.mark_source === 'reviewer'
                        ? 'Set by a reviewer. Re-classifying will not overwrite it.'
                        : brand?.mark_source === 'reference'
                          ? 'Matched automatically against league, team and venue records. Choose a value to override it.'
                          : 'Counts already \u2014 nothing to do unless it is wrong. Choose Commercial brand to pin it so re-classifying cannot change it.'}
                  </p>
                </div>
              )}

              {/* Zone is the one field a reviewer looking at the frame is better
                  placed to judge than the model. Phase 0: 66.7% zone accuracy,
                  and 14 of 16 errors were backstop read as infield signage. */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label htmlFor="zone-picker"
                         className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />Placement
                  </label>
                  {savingZone && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                </div>

                <select
                  id="zone-picker"
                  value={active.zone_code}
                  disabled={readOnly || savingZone || zones.length === 0}
                  onChange={(e) => void changeZone(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-800 disabled:opacity-50"
                >
                  {/* keep the current value selectable even if it is inactive */}
                  {zones.length === 0 && <option value={active.zone_code}>{active.zone_code}</option>}
                  {/* Alphabetical, like the two pickers above it.
                      This overrides a server ranking that puts sport-specific
                      zones ahead of universal ones, which is the right order
                      for BROWSING. Correcting a zone is not browsing: the
                      reviewer is looking at the frame, already knows it says
                      backstop, and wants to find that word. Safe because the
                      server FILTERS by sport in the WHERE clause as well, so
                      a basketball list still cannot offer dugout - only the
                      ranking is lost, never the scoping.

                      Copied before sorting: `zones` is state, and sort
                      mutates in place. */}
                  {[...zones]
                    .sort((a, b) =>
                      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                    .map((z) => (
                      <option key={z.code} value={z.code}>{z.label}</option>
                    ))}
                </select>

                {zones.find((z) => z.code === active.zone_code)?.description && (
                  <p className="text-[11px] text-gray-500 mt-1.5 leading-snug">
                    {zones.find((z) => z.code === active.zone_code)!.description}
                  </p>
                )}

                {active.zone_code_original && active.zone_code_original !== active.zone_code && (
                  <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
                    <p className="text-[11px] text-amber-700">
                      Corrected — the model read this as{' '}
                      <span className="font-medium">
                        {zones.find((z) => z.code === active.zone_code_original)?.label
                          ?? active.zone_code_original}
                      </span>
                    </p>
                    {!readOnly && (
                      <button
                        type="button"
                        disabled={savingZone}
                        onClick={() => void changeZone(active.zone_code_original!)}
                        className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 disabled:opacity-40 flex-shrink-0"
                      >
                        <Undo2 className="h-3 w-3" />Revert
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {source.setAppearanceExclusion && !readOnly && !sponsored && (
            <div className="flex items-center justify-end gap-2 mb-1.5 text-xs">
              <label htmlFor="appearance-reason" className="text-gray-500">Exclude single appearances as</label>
              <select id="appearance-reason" value={appearanceReason}
                      onChange={(e) => setAppearanceReason(e.target.value as ExclusionReason)}
                      className="border border-gray-300 rounded px-1.5 py-0.5 bg-white text-gray-800">
                {REASONS.filter((r) => r.value !== 'own_mark').map((r) =>
                  <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          )}
          <BrandAppearanceTable
            rows={rows}
            active={active}
            zones={zones}
            onSelect={select}
            excludingId={excludingId}
            // Passing nothing is what removes the control, so a sponsor or
            // a read-only surface cannot end up with a disabled button
            // instead of no button.
            onToggleExclude={source.setAppearanceExclusion && !readOnly && !sponsored
              ? (id, gone) => { void toggleAppearance(id, gone) }
              : undefined}
          />
        </div>
      )}
    </Drawer>
  )
}

export default BroadcastBrandDrawer
